/**
 * 知识库引用标记组件
 * 支持点击展开内联预览，以及打开右侧文档抽屉
 * 根据引用类型显示不同颜色（国家法律-蓝色、行政法规-绿色、知识库-紫色、判例法-橙色）
 */

import React, { useState, useEffect } from 'react';
import { BookOpen, ChevronRight, Loader2, Scale, Gavel, FileText } from 'lucide-react';
import { InlinePreview } from './InlinePreview';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001';

// ==================== 引用类型和样式定义 ====================

export type LegalCitationType = 'national_law' | 'regulation' | 'case_law' | 'knowledge_base';

interface CitationStyle {
    Icon: React.ElementType;
    bgClass: string;
    hoverBgClass: string;
    textClass: string;
    hoverTextClass: string;
    borderClass: string;
    hoverBorderClass: string;
    ringClass: string;
}

// 根据设计文档定义的颜色方案
const CITATION_STYLES: Record<LegalCitationType, CitationStyle> = {
    // 国家法律 - 蓝色 (民法典, 刑法, 劳动法等)
    national_law: {
        Icon: BookOpen,
        bgClass: 'from-blue-50 to-indigo-50',
        hoverBgClass: 'hover:from-blue-100 hover:to-indigo-100',
        textClass: 'text-blue-700',
        hoverTextClass: 'hover:text-blue-800',
        borderClass: 'border-blue-200',
        hoverBorderClass: 'hover:border-blue-300',
        ringClass: 'ring-blue-400/50',
    },
    // 行政法规/条例 - 绿色 (交通运输部第x号令等)
    regulation: {
        Icon: Gavel,
        bgClass: 'from-green-50 to-emerald-50',
        hoverBgClass: 'hover:from-green-100 hover:to-emerald-100',
        textClass: 'text-green-700',
        hoverTextClass: 'hover:text-green-800',
        borderClass: 'border-green-200',
        hoverBorderClass: 'hover:border-green-300',
        ringClass: 'ring-green-400/50',
    },
    // 判例法 - 橙色 ((2025) 京01民终... 等)
    case_law: {
        Icon: Scale,
        bgClass: 'from-orange-50 to-amber-50',
        hoverBgClass: 'hover:from-orange-100 hover:to-amber-100',
        textClass: 'text-orange-700',
        hoverTextClass: 'hover:text-orange-800',
        borderClass: 'border-orange-200',
        hoverBorderClass: 'hover:border-orange-300',
        ringClass: 'ring-orange-400/50',
    },
    // 知识库/用户文档 - 紫色
    knowledge_base: {
        Icon: FileText,
        bgClass: 'from-purple-50 to-fuchsia-50',
        hoverBgClass: 'hover:from-purple-100 hover:to-fuchsia-100',
        textClass: 'text-purple-700',
        hoverTextClass: 'hover:text-purple-800',
        borderClass: 'border-purple-200',
        hoverBorderClass: 'hover:border-purple-300',
        ringClass: 'ring-purple-400/50',
    },
};

// 检测引用类型
// 根据规范：用户知识库=紫色，Omnisearch结果根据类型分色
// 由于当前系统仅使用用户知识库，默认返回 knowledge_base (紫色)
// 未来当 Omnisearch 集成后，需要根据 sourceType 属性区分
const detectCitationType = (docName: string, sourceType?: 'knowledge_base' | 'omnisearch_case' | 'omnisearch_statute' | 'omnisearch_regulation' | 'omnisearch_secondary' | 'omnisearch_general'): LegalCitationType => {
    // 如果明确指定了来源类型，使用映射
    if (sourceType) {
        switch (sourceType) {
            case 'knowledge_base':
                return 'knowledge_base'; // 紫色
            case 'omnisearch_case':
                return 'case_law'; // 橙色 (法院案例)
            case 'omnisearch_statute':
                return 'national_law'; // 蓝色 (法规法典)
            case 'omnisearch_regulation':
                return 'regulation'; // 绿色 (条例)
            case 'omnisearch_secondary':
            case 'omnisearch_general':
            default:
                return 'knowledge_base'; // 紫色 (默认/二级来源)
        }
    }

    // 没有指定来源类型时，默认为用户知识库 (紫色)
    // 注意：这是当前实现的默认行为，因为系统暂未集成 Omnisearch
    return 'knowledge_base';
};


export interface CitationSource {
    sourceId: string;
    sourceName: string;
    pageNumber?: number;
    totalPages?: number;
    chunkIndex?: number;
    previewText?: string;
}

export interface CitationTagProps {
    docIndex: number;
    docName?: string;
    sourceId?: string;
    chunkIndex?: number;
    // 可选：从外部传入的预览数据
    previewData?: CitationSource;
    // 回调：点击"查看完整文档"时触发
    onViewFullDocument?: (source: CitationSource) => void;
    // 可选：强制指定引用类型
    citationType?: LegalCitationType;
}

export const CitationTag: React.FC<CitationTagProps> = ({
    docIndex,
    docName,
    sourceId,
    chunkIndex,
    previewData,
    onViewFullDocument,
    citationType,
}) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [citationData, setCitationData] = useState<CitationSource | null>(previewData || null);
    const [realDocName, setRealDocName] = useState<string | null>(docName || null);

    // 如果没有提供 docName，尝试从后端获取
    const displayName = realDocName || docName || `用户文档 ${docIndex}`;
    // 优先使用传入的 sourceId，否则回退到生成的 ID
    const documentId = sourceId || `doc-${docIndex}`;

    // 检测引用类型（优先使用传入的类型，否则根据文档名自动检测）
    const effectiveCitationType = citationType || detectCitationType(displayName);
    const style = CITATION_STYLES[effectiveCitationType];

    // 组件挂载时尝试获取文档名称（仅当没有提供 docName 和 sourceId 时）
    React.useEffect(() => {
        if (!docName && !realDocName && !sourceId) {
            fetchDocumentName();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [docName, sourceId]);

    // 获取用户ID（兼容访客和登录用户）
    const getUserId = (): string | null => {
        // 优先从 token 解析用户 ID (登录用户优先)
        const token = localStorage.getItem('auth_token');
        if (token) {
            try {
                const payload = JSON.parse(atob(token.split('.')[1]));
                const userId = payload.sub || payload.userId || payload.email;
                if (userId) return userId;
            } catch {
                // Token 解析失败，继续尝试其他方式
            }
        }
        // 回退到访客 ID
        const guestId = localStorage.getItem('guest_user_id');
        if (guestId) return guestId;
        return null;
    };

    // 获取文档名称（仅用于旧格式引用，没有 sourceId 时）
    const fetchDocumentName = async () => {
        try {
            const token = localStorage.getItem('auth_token');
            const userId = getUserId();
            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            if (userId) {
                headers['x-user-id'] = userId;
            }

            // 获取文档列表
            const res = await fetch(
                `${API_BASE_URL}/api/knowledge/documents`,
                { headers }
            );
            const response = await res.json();

            if (response.success && response.documents) {
                // 找到第 docIndex 个文档
                const doc = response.documents[docIndex - 1];
                if (doc && doc.name) {
                    setRealDocName(doc.name);
                }
            }
        } catch (error) {
            console.error('Failed to fetch document name:', error);
        }
    };

    // 获取引用详情（chunks）
    const fetchCitationDetails = async () => {
        if (citationData) return;

        // 如果已经有 previewData，直接使用它
        if (previewData) {
            setCitationData(previewData);
            return;
        }

        // 检查 documentId 是否是自动生成的格式（doc-N），这种情况下不需要尝试获取
        // 因为这种 ID 在 Qdrant 中不存在
        const isAutoGeneratedId = /^doc-\d+$/.test(documentId);
        if (isAutoGeneratedId && !sourceId) {
            console.warn('[CitationTag] Auto-generated ID detected without sourceId, skipping fetch');
            setCitationData({
                sourceId: documentId,
                sourceName: displayName,
                chunkIndex: chunkIndex || 0,
                previewText: '未能预览文档内容。',
            });
            return;
        }

        setIsLoading(true);
        console.log('[CitationTag] fetchCitationDetails called with:', { docIndex, docName, sourceId, documentId });

        try {
            const token = localStorage.getItem('auth_token');
            const userId = getUserId();
            console.log('[CitationTag] User ID resolved:', userId);

            const headers: Record<string, string> = {};
            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }
            if (userId) {
                headers['x-user-id'] = userId;
            }

            let realDocId = documentId;
            let docNameFromList = displayName;

            // 如果有传入的 sourceId，直接使用它获取 chunks
            if (sourceId) {
                realDocId = sourceId;
                docNameFromList = docName || displayName;
                console.log('[CitationTag] Using provided sourceId:', realDocId);
            } else {
                console.log('[CitationTag] No sourceId provided, fetching document list...');
                // 获取文档列表，通过名称或索引查找
                const docsRes = await fetch(
                    `${API_BASE_URL}/api/knowledge/documents`,
                    { headers }
                );
                const docsResponse = await docsRes.json();
                console.log('[CitationTag] Document list response:', docsResponse);

                if (docsResponse.success && docsResponse.documents) {
                    // 规范化名称用于匹配（去除扩展名、空格、常见前缀）
                    const normalizeName = (name: string) => {
                        return name
                            .replace(/\.[^.]+$/, '') // 去除扩展名
                            .replace(/中华人民共和国/g, '') // 去除常见前缀
                            .replace(/[-_\s]/g, '') // 去除分隔符
                            .toLowerCase();
                    };

                    const searchName = docName ? normalizeName(docName) : '';
                    console.log('[CitationTag] Searching for document with normalized name:', searchName);

                    // 通过名称匹配文档
                    let doc = docName
                        ? docsResponse.documents.find((d: { name: string }) => {
                            const normalizedDocName = normalizeName(d.name);
                            // 精确匹配
                            if (normalizedDocName === searchName) return true;
                            // 包含匹配（双向）
                            if (normalizedDocName.includes(searchName) || searchName.includes(normalizedDocName)) return true;
                            // 去年份后匹配 (如 "国防交通法-2016" -> "国防交通法")
                            const nameWithoutYear = searchName.replace(/-?\d{4}$/, '');
                            if (normalizedDocName.includes(nameWithoutYear) || nameWithoutYear.includes(normalizedDocName)) return true;
                            return false;
                        })
                        : null;

                    // 回退到索引匹配
                    if (!doc && docIndex > 0) {
                        doc = docsResponse.documents[docIndex - 1];
                    }

                    if (doc) {
                        realDocId = doc.id;
                        docNameFromList = doc.name;
                        console.log('[CitationTag] Found document:', { id: doc.id, name: doc.name });
                        if (!realDocName) {
                            setRealDocName(doc.name);
                        }
                    } else {
                        console.warn('[CitationTag] No matching document found in list');
                    }
                }
            }

            // 获取文档分块信息
            console.log('[CitationTag] Fetching chunks for:', realDocId);
            const res = await fetch(
                `${API_BASE_URL}/api/knowledge/documents/${realDocId}/chunks`,
                { headers }
            );
            const response = await res.json();
            console.log('[CitationTag] Chunks response:', { status: res.status, success: response.success, chunkCount: response.chunks?.length });

            if (response.success && response.chunks && response.chunks.length > 0) {
                // 使用指定的 chunkIndex 或默认第一个
                const targetChunk = chunkIndex !== undefined
                    ? response.chunks.find((c: { chunkIndex: number }) => c.chunkIndex === chunkIndex) || response.chunks[0]
                    : response.chunks[0];

                setCitationData({
                    sourceId: realDocId,
                    sourceName: docNameFromList,
                    pageNumber: targetChunk.pageNumber,
                    totalPages: response.totalPages,
                    chunkIndex: targetChunk.chunkIndex,
                    previewText: targetChunk.content,
                });
            } else {
                // 文档不存在或无内容
                console.warn('[CitationTag] No chunks found, setting "deleted" message');
                setCitationData({
                    sourceId: realDocId,
                    sourceName: docNameFromList,
                    chunkIndex: chunkIndex || 0,
                    previewText: '该文档已被删除或暂无内容',
                });
            }
        } catch (error) {
            console.error('[CitationTag] Failed to fetch citation details:', error);
            setCitationData({
                sourceId: documentId,
                sourceName: displayName,
                chunkIndex: chunkIndex || 0,
                previewText: '无法加载预览内容，请检查网络连接',
            });
        } finally {
            setIsLoading(false);
        }
    };

    // 点击展开/收起
    const handleToggle = () => {
        if (!isExpanded && !citationData) {
            fetchCitationDetails();
        }
        setIsExpanded(!isExpanded);
    };

    // 打开完整文档
    const handleViewFullDocument = () => {
        if (citationData && onViewFullDocument) {
            onViewFullDocument(citationData);
        }
        setIsExpanded(false);
    };

    return (
        <span className="inline">
            {/* 引用标记按钮 - 根据引用类型使用不同颜色 */}
            <button
                onClick={handleToggle}
                className={`inline-flex items-center gap-1 px-2 py-0.5 mx-0.5
                   bg-gradient-to-r ${style.bgClass}
                   ${style.hoverBgClass}
                   ${style.textClass} ${style.hoverTextClass}
                   border ${style.borderClass} ${style.hoverBorderClass}
                   rounded-md text-sm font-medium
                   transition-all duration-200
                   shadow-sm hover:shadow cursor-pointer
                   ${isExpanded ? `ring-2 ${style.ringClass}` : ''}`}
                title={`点击${isExpanded ? '收起' : '展开'}预览 - ${displayName}`}
            >
                {isLoading ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                    <style.Icon className="w-3 h-3" />
                )}
                <span>{displayName}</span>
                <ChevronRight
                    className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''
                        }`}
                />
            </button>

            {/* 内联预览 */}
            {isExpanded && citationData && (
                <InlinePreview
                    isExpanded={isExpanded}
                    documentId={citationData.sourceId}
                    documentName={citationData.sourceName}
                    pageNumber={citationData.pageNumber}
                    totalPages={citationData.totalPages}
                    chunkIndex={citationData.chunkIndex}
                    previewText={citationData.previewText || ''}
                    onViewFullDocument={handleViewFullDocument}
                    onClose={() => setIsExpanded(false)}
                />
            )}
        </span>
    );
};

export default CitationTag;
